import { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { simulateProgression, SimulationResult } from '../../../../../utils/progressionSimulation';

interface Props {
  show: boolean;
  onHide: () => void;
  defaultGroups: number;
  defaultPlayers: number;
  defaultMoveCount: number;
}

export const ProgressionSimulatorModal = ({
  show,
  onHide,
  defaultGroups,
  defaultPlayers,
  defaultMoveCount,
}: Props) => {
  const { t } = useTranslation();

  const [numGroups, setNumGroups] = useState(defaultGroups || 4);
  const [numPlayers, setNumPlayers] = useState(defaultPlayers || 20);
  const [moveCount, setMoveCount] = useState(defaultMoveCount || 1);
  const [result, setResult] = useState<SimulationResult | null>(null);

  useEffect(() => {
    if (show) {
      setNumGroups(Number(defaultGroups) || 4);
      setNumPlayers(Number(defaultPlayers) || 20);
      setMoveCount(Number(defaultMoveCount) || 1);
    }
  }, [show, defaultGroups, defaultPlayers, defaultMoveCount]);

  useEffect(() => {
    const sim = simulateProgression(numGroups, numPlayers, moveCount);
    setResult(sim);
  }, [numGroups, numPlayers, moveCount]);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('progression-simulator-title', 'Box League Progression Simulator')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: '#f8f9fa' }}>
        <div className="bg-white p-3 rounded shadow-sm mb-3 d-flex flex-wrap align-items-center gap-4 border">
          <div className="d-flex align-items-center gap-2">
            <strong className="small text-uppercase text-muted">Simulation Context:</strong>
            <span className="badge bg-info text-dark border">{numGroups} Boxes</span>
          </div>

          <Form.Group className="d-flex">
            <Form.Label className="small font-weight-bold text-muted mb-0">Test with Players:</Form.Label>
            <Form.Control
              type="number"
              value={numPlayers}
              onChange={(e) => setNumPlayers(Number(e.target.value))}
              min={2}
            />
          </Form.Group>
          {/*
          <Form.Group>
            <Form.Label className="small font-weight-bold">Players moving Up/Down</Form.Label>
            <Form.Control
              type="number"
              value={moveCount}
              onChange={(e) => setMoveCount(Number(e.target.value))}
              min={1}
            />
          </Form.Group>
          */}

          <div className="small text-muted ms-auto">Changes are applied automatically</div>
        </div>

        {result && (
          <div className="row">
            <div className="col-md-6">
              <h6 className="text-muted text-uppercase small font-weight-bold mb-3">
                Round 1 Results (Mock)
              </h6>
              <div className="row">
                {Object.entries(result.mockStandings).map(([groupId, players]) => (
                  <div key={groupId} className="col-12 mb-2">
                    <div className="card shadow-sm border-0">
                      <div className="card-header bg-white py-1 small font-weight-bold">
                        Box {Number(groupId + 1)}{' '}
                        <span className="text-muted font-weight-normal">
                          ({players.length} players)
                        </span>
                      </div>
                      <div className="card-body p-2">
                        {players.map((p) => (
                          <div
                            key={p.userId}
                            className="d-flex justify-content-between align-items-center small border-bottom py-1"
                            style={{ opacity: p.isAllocated ? 1 : 0.6 }}
                          >
                            <span>
                              <span className="badge badge-dark border mr-2">#{p.rank}</span>
                              Player {p.userId}
                            </span>
                            <div>
                              {p.isPromoted && (
                                <span className="badge badge-success">Promoted</span>
                              )}
                              {p.isRelegated && (
                                <span className="badge badge-danger">Relegated</span>
                              )}
                              {!p.isAllocated && (
                                <span className="badge badge-light text-muted">Stays</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="col-md-6">
              <h6 className="text-primary text-uppercase small font-weight-bold mb-3">
                Round 2 Projection
              </h6>
              <div className="row">
                {result.targetGroups.map((group, idx) => (
                  <div key={idx} className="col-12 mb-2">
                    <div
                      className="card border-primary shadow-sm"
                      style={{ backgroundColor: '#f0f7ff' }}
                    >
                      <div className="card-header bg-transparent border-primary py-1 small font-weight-bold text-primary d-flex justify-content-between">
                        <span>{group.groupName}</span>
                        <span>{group.players.length} Players</span>
                      </div>
                      <div className="card-body p-2">
                        <div className="row">
                          {group.players.map((p) => (
                            <div key={p.userId} className="col-6 mb-1">
                              <div className="bg-white border rounded p-1 small d-flex flex-column">
                                <span className="font-weight-bold">Player {p.userId}</span>
                                <span className="text-muted" style={{ fontSize: '0.7em' }}>
                                  Was Rank #{p.rank}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('button-close', 'Close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
